import { Slot } from "@radix-ui/react-slot";
import { domMax, LazyMotion, m, useReducedMotion } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { Icon } from "../../icon";
import { Ripple, useRippleState } from "../../ripple";
import { StateLayer } from "../../shared/state-layer";
import { TouchTarget } from "../../shared/touch-target";
import { useSplitButtonContext } from "./split-button-context";
import {
	SPLIT_BUTTON_CHEVRON_SPRING,
	SPLIT_BUTTON_RADIUS_SPRING,
	SPLIT_BUTTON_TOKENS,
} from "./split-button-tokens";
import type { SplitButtonTrailingProps } from "./split-button-types";
import { splitButtonVariants } from "./split-button-variants";

const SplitButtonTrailingComponent = React.forwardRef<
	HTMLButtonElement,
	SplitButtonTrailingProps
>(
	(
		{
			className,
			style,
			variant = "filled",
			size = "sm",
			checked,
			onCheckedChange,
			icon,
			disabled,
			onPointerDown,
			onPointerUp,
			onPointerLeave,
			onPointerCancel,
			onFocus,
			onBlur,
			asChild = false,
			children,
			"aria-label": ariaLabel,
			"aria-haspopup": ariaHaspopup = "menu",
			...restProps
		},
		ref,
	) => {
		const context = useSplitButtonContext();
		const actualSize = context?.size ?? size;
		const actualVariant = context?.variant ?? variant;

		const [isHovered, setIsHovered] = React.useState(false);
		const [isFocused, setIsFocused] = React.useState(false);
		const [isPressed, setIsPressed] = React.useState(false);

		const tokens = SPLIT_BUTTON_TOKENS[actualSize];
		const prefersReducedMotion = useReducedMotion();
		const needsTouchTarget = actualSize === "xs" || actualSize === "sm";

		const {
			ripples,
			onPointerDown: onRipplePointerDown,
			removeRipple,
		} = useRippleState({ disabled });

		const innerCornerRadius =
			isPressed || isFocused
				? tokens.innerCorner.pressed
				: isHovered
					? tokens.innerCorner.hovered
					: tokens.innerCorner.default;

		const outerRadius =
			(tokens.containerHeight * tokens.outerCornerPercent) / 100;

		// Format: TL TR BR BL
		// If checked (menu open), it morphs into a full circle (pill)
		// If unchecked, the left inner corners are animated, right outer are pill
		const borderRadius = checked
			? `${outerRadius}px ${outerRadius}px ${outerRadius}px ${outerRadius}px`
			: `${innerCornerRadius}px ${outerRadius}px ${outerRadius}px ${innerCornerRadius}px`;

		const handleToggle = React.useCallback(() => {
			if (!disabled) {
				onCheckedChange(!checked);
			}
		}, [disabled, checked, onCheckedChange]);

		const handlePointerDown = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				onRipplePointerDown(e);
				setIsPressed(true);
				onPointerDown?.(e);
			},
			[onRipplePointerDown, onPointerDown],
		);

		const handlePointerUp = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerUp?.(e);
			},
			[onPointerUp],
		);

		const handlePointerEnter = React.useCallback(() => {
			if (!disabled) {
				setIsHovered(true);
			}
		}, [disabled]);

		const handlePointerLeave = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsHovered(false);
				setIsPressed(false);
				onPointerLeave?.(e);
			},
			[onPointerLeave],
		);

		const handlePointerCancel = React.useCallback(
			(e: React.PointerEvent<HTMLButtonElement>) => {
				setIsPressed(false);
				onPointerCancel?.(e);
			},
			[onPointerCancel],
		);

		const handleFocus = React.useCallback(
			(e: React.FocusEvent<HTMLButtonElement>) => {
				if (!disabled) setIsFocused(true);
				onFocus?.(e);
			},
			[disabled, onFocus],
		);

		const handleBlur = React.useCallback(
			(e: React.FocusEvent<HTMLButtonElement>) => {
				setIsFocused(false);
				onBlur?.(e);
			},
			[onBlur],
		);

		const innerContent = (
			<>
				{needsTouchTarget && <TouchTarget />}
				<StateLayer
					hovered={isHovered}
					focused={isFocused}
					pressed={isPressed}
				/>
				<Ripple ripples={ripples} onRippleDone={removeRipple} />

				<m.div
					animate={{
						rotate: checked ? 180 : 0,
						x: checked ? 0 : tokens.opticalCenterOffset,
					}}
					transition={
						prefersReducedMotion ? { duration: 0 } : SPLIT_BUTTON_CHEVRON_SPRING
					}
					className="flex items-center justify-center shrink-0 [&>svg]:w-full [&>svg]:h-full"
					style={{
						width: tokens.trailingIconSize,
						height: tokens.trailingIconSize,
					}}
				>
					{icon ?? (
						<Icon
							name="arrow_drop_down"
							size={tokens.trailingIconSize}
							opticalSize={24}
						/>
					)}
				</m.div>
			</>
		);

		const containerClassName = cn(
			splitButtonVariants({
				variant: actualVariant,
				size: actualSize,
				disabled,
			}),
			className,
		);

		const mergedStyle = {
			paddingInlineStart: tokens.trailingPadding.start,
			paddingInlineEnd: tokens.trailingPadding.end,
			borderRadius,
			...(style as React.CSSProperties),
		};

		return (
			<LazyMotion features={domMax} strict>
				{asChild ? (
					<Slot
						ref={ref}
						data-split-button-part="trailing"
						aria-label={ariaLabel}
						aria-expanded={checked}
						aria-haspopup={ariaHaspopup}
						aria-disabled={disabled}
						onClick={handleToggle}
						onPointerDown={handlePointerDown}
						onPointerUp={handlePointerUp}
						onPointerEnter={handlePointerEnter}
						onPointerLeave={handlePointerLeave}
						onPointerCancel={handlePointerCancel}
						onFocus={handleFocus}
						onBlur={handleBlur}
						style={mergedStyle}
						className={containerClassName}
						{...(restProps as React.HTMLAttributes<HTMLElement>)}
					>
						{React.cloneElement(
							React.Children.only(children) as React.ReactElement<{
								children?: React.ReactNode;
							}>,
							{ children: innerContent },
						)}
					</Slot>
				) : (
					<m.button
						ref={ref}
						type="button"
						data-split-button-part="trailing"
						aria-label={ariaLabel}
						aria-expanded={checked}
						aria-haspopup={ariaHaspopup}
						aria-disabled={disabled}
						disabled={disabled}
						onClick={handleToggle}
						onPointerDown={handlePointerDown}
						onPointerUp={handlePointerUp}
						onPointerEnter={handlePointerEnter}
						onPointerLeave={handlePointerLeave}
						onPointerCancel={handlePointerCancel}
						onFocus={handleFocus}
						onBlur={handleBlur}
						animate={{ borderRadius }}
						transition={SPLIT_BUTTON_RADIUS_SPRING}
						style={{
							paddingInlineStart: tokens.trailingPadding.start,
							paddingInlineEnd: tokens.trailingPadding.end,
							...style,
						}}
						className={containerClassName}
						{...restProps}
					>
						{innerContent}
					</m.button>
				)}
			</LazyMotion>
		);
	},
);

SplitButtonTrailingComponent.displayName = "SplitButtonTrailing";

export const SplitButtonTrailing = React.memo(SplitButtonTrailingComponent);

// ─────────────────────────────────────────────────────────────────────────────
// Preset Components
// ─────────────────────────────────────────────────────────────────────────────

export const FilledSplitButtonTrailing = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingProps, "variant">
>((props, ref) => (
	<SplitButtonTrailing ref={ref} variant="filled" {...props} />
));
FilledSplitButtonTrailing.displayName = "FilledSplitButtonTrailing";

export const TonalSplitButtonTrailing = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingProps, "variant">
>((props, ref) => <SplitButtonTrailing ref={ref} variant="tonal" {...props} />);
TonalSplitButtonTrailing.displayName = "TonalSplitButtonTrailing";

export const ElevatedSplitButtonTrailing = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingProps, "variant">
>((props, ref) => (
	<SplitButtonTrailing ref={ref} variant="elevated" {...props} />
));
ElevatedSplitButtonTrailing.displayName = "ElevatedSplitButtonTrailing";

export const OutlinedSplitButtonTrailing = React.forwardRef<
	HTMLButtonElement,
	Omit<SplitButtonTrailingProps, "variant">
>((props, ref) => (
	<SplitButtonTrailing ref={ref} variant="outlined" {...props} />
));
OutlinedSplitButtonTrailing.displayName = "OutlinedSplitButtonTrailing";
